import { withPersistedQueries } from '@amazeelabs/silverback-graphql-persisted';
import { feedbackMachine } from '@luks/meta/src';
import { buildForm, KitchenSinkRoute, RouteSlotInput } from '@luks/ui';
import { useActor } from '@xstate/react';
import { GraphQLClient } from 'graphql-request';
import { interpret } from 'xstate';

import { getSdk } from '../../generated/mutations';
import queryMap from '../../generated/persisted-queries-map.json';

const sdk = getSdk(
  new GraphQLClient(process.env.GATSBY_GRAPHQL_ENDPOINT!, {
    fetch: withPersistedQueries(fetch, queryMap),
  }),
);

type Context = typeof feedbackMachine['context'];

const service = interpret(
  feedbackMachine.withConfig({
    services: {
      submitFeedback: async (ctx) => {
        const res = await sdk.HelpfulnessFeedback({
          wasHelpful: !!ctx.wasHelpful,
          url: window.location.href,
        });
        return res.addPageFeedback.submissionId!;
      },
      submitComment: async (ctx) => {
        return (
          (
            await sdk.HelpfulnessComment({
              id: ctx.submissionId!,
              email: ctx.email!,
              comment: ctx.comment!,
            })
          ).addCommentToPageFeedback === 'Success'
        );
      },
    },
  }),
);

service.start();

const FeedbackForm = buildForm<Required<Pick<Context, 'wasHelpful'>>>({
  initialValues: { wasHelpful: false },
  onSubmit(values) {
    service.send({
      type: 'FEEDBACK',
      ...values,
    });
  },
});

const CommentForm = buildForm<Required<Pick<Context, 'email' | 'comment'>>>({
  initialValues: {
    comment: '',
    email: '',
  },
  onSubmit(values) {
    service.send({
      type: 'COMMENT',
      ...values,
    });
  },
});

export function helpfulness(): RouteSlotInput<
  typeof KitchenSinkRoute,
  'body',
  'Helpfulness'
> {
  service.send({ type: 'RESET' });
  return {
    key: 'Helpfulness',
    input: function useHelpfulness() {
      const [state] = useActor(service);
      const currentState = state.hasTag('loading')
        ? 2
        : state.matches('feedback')
        ? 0
        : state.matches('comment')
        ? 1
        : state.matches('done')
        ? 3
        : 0;

      return [{ CommentForm, FeedbackForm }, currentState];
    },
  };
}
